/**
 * @suite/settings — the shared two-column settings window shell.
 *
 * The suite's ONE settings chrome, modeled on the Kor panel: a slim header,
 * a left section nav and a scrollable right content column, rendered inside
 * a SEPARATE window (`?view=settings`). Every application hosts its own
 * section content in `children`; the layout, geometry and theming live here
 * (styled by the served `suite-settings.css` — edited once, inherited
 * everywhere).
 */

import { type ReactNode, useCallback } from "react";
import { closeSettingsWindow } from "./openSettings";

/** One entry of the left nav. */
export interface SettingsWindowSection {
    /** Stable key — also the `&tab=` deep-link value */
    key: string;
    label: string;
    /** Optional leading icon: any renderable (an inline SVG, a Lucide icon) */
    icon?: ReactNode;
}

export interface SettingsWindowProps {
    /** The application's display name, shown next to the "Settings" title */
    appLabel: string;
    /** Left-nav entries, in display order */
    sections: readonly SettingsWindowSection[];
    /** The active section key (the page owns the state and the deep link) */
    activeKey: string;
    onActivate: (key: string) => void;
    /** The active section's content, rendered into the right column */
    children: ReactNode;
    /** Optional one-line footer under the content (autosave note, version) */
    footer?: ReactNode;
    /** Let wide embedded panels (tables, canvases) exceed the content cap */
    wideContent?: boolean;
}

function CloseIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
        </svg>
    );
}

export function SettingsWindow({
    appLabel,
    sections,
    activeKey,
    onActivate,
    children,
    footer,
    wideContent = false,
}: SettingsWindowProps) {
    const handleClose = useCallback(() => closeSettingsWindow(), []);

    return (
        <div className="suite-settings-window">
            <header className="suite-settings-header">
                <div className="suite-settings-heading">
                    <h1 className="suite-settings-title">Settings</h1>
                    <span className="suite-settings-subtitle">{appLabel}</span>
                </div>
                <button
                    type="button"
                    className="suite-settings-close"
                    onClick={handleClose}
                    aria-label="Close settings"
                    title="Close"
                >
                    <CloseIcon />
                </button>
            </header>
            <div className="suite-settings-body">
                <nav className="suite-settings-nav" aria-label="Settings sections">
                    {sections.map((section) => {
                        const active = section.key === activeKey;
                        return (
                            <button
                                key={section.key}
                                type="button"
                                className={
                                    active
                                        ? "suite-settings-nav-item is-active"
                                        : "suite-settings-nav-item"
                                }
                                aria-current={active ? "page" : undefined}
                                onClick={() => onActivate(section.key)}
                            >
                                {section.icon ? (
                                    <span className="suite-settings-nav-icon" aria-hidden="true">
                                        {section.icon}
                                    </span>
                                ) : null}
                                <span className="suite-settings-nav-label">{section.label}</span>
                            </button>
                        );
                    })}
                </nav>
                <div className="suite-settings-main">
                    <main
                        className={
                            wideContent
                                ? "suite-settings-content suite-settings-content--wide"
                                : "suite-settings-content"
                        }
                    >
                        {children}
                    </main>
                    {footer ? (
                        <footer className="suite-settings-footer">{footer}</footer>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
